// React Query hooks for the WB stock surface (Phase C8).
//
// Pairs with /api/v1/wb/stock/* and /api/v1/wb/stock-alerts/*. Stock is
// READ-ONLY — there are no manual-adjustment endpoints. The only write
// is `dismissStockAlert` (POST /wb/stock-alerts/:enc_id/dismiss).
//
// `BalanceDto` is non-paginated (server returns the full list); movements
// and alerts use the standard pagination envelope.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Shared types -----------------------------------------------------------

export type StockUnit = 'tonne' | 'kg' | 'each' | 'm3';
export type ThresholdStatus = 'ok' | 'low' | 'high' | 'not_tracked';
export type MovementDirection = 'in' | 'out';
export type StockAlertStatus = 'active' | 'dismissed' | 'auto_resolved';
export type StockAlertType = 'low' | 'high';

export interface StockProductRef {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface StockYardRef {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface StockTicketRef {
  readonly enc_id: string;
  readonly ticket_number: string | null;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

// -- Balances ---------------------------------------------------------------

export interface BalanceDto {
  readonly product: StockProductRef;
  readonly yard: StockYardRef;
  readonly balance: number;
  readonly unit_of_measure: StockUnit;
  readonly threshold_status: ThresholdStatus;
  readonly stock_threshold_low: number | null;
  readonly stock_threshold_high: number | null;
  readonly last_movement_at: string;
}

interface ListBalancesParams {
  yard_enc_id?: string;
  product_enc_id?: string;
}

interface ListBalancesResponse {
  balances: BalanceDto[];
}

function balancesQs(params: ListBalancesParams): string {
  const sp = new URLSearchParams();
  if (params.yard_enc_id) sp.set('yard_enc_id', params.yard_enc_id);
  if (params.product_enc_id) sp.set('product_enc_id', params.product_enc_id);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function useWbStockBalances(params: ListBalancesParams = {}) {
  return useQuery<ListBalancesResponse>({
    queryKey: queryKeys.wb.stockBalances(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ListBalancesResponse>(
        `${endpoints.wb.stockBalances}${balancesQs(params)}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

// -- Movements --------------------------------------------------------------

export interface MovementDto {
  readonly enc_id: string;
  readonly product: StockProductRef;
  readonly yard: StockYardRef;
  readonly direction: MovementDirection;
  readonly quantity: number;
  readonly unit_of_measure: StockUnit;
  readonly balance_after: number;
  readonly ticket: StockTicketRef | null;
  readonly occurred_at: string;
}

export interface ListMovementsParams {
  yard_enc_id?: string;
  product_enc_id?: string;
  direction?: MovementDirection;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

interface ListMovementsResponse {
  movements: MovementDto[];
  pagination: Pagination;
}

function movementsQs(params: ListMovementsParams): string {
  const sp = new URLSearchParams();
  if (params.yard_enc_id) sp.set('yard_enc_id', params.yard_enc_id);
  if (params.product_enc_id) sp.set('product_enc_id', params.product_enc_id);
  if (params.direction) sp.set('direction', params.direction);
  if (params.date_from) sp.set('date_from', params.date_from);
  if (params.date_to) sp.set('date_to', params.date_to);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function useWbStockMovements(params: ListMovementsParams = {}) {
  return useQuery<ListMovementsResponse>({
    queryKey: queryKeys.wb.stockMovements(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ListMovementsResponse>(
        `${endpoints.wb.stockMovements}${movementsQs(params)}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

// -- Alerts -----------------------------------------------------------------

export interface StockAlertDto {
  readonly enc_id: string;
  readonly product: StockProductRef & {
    readonly unit_of_measure: StockUnit;
    readonly stock_threshold_low: number | null;
    readonly stock_threshold_high: number | null;
  };
  readonly yard: StockYardRef;
  readonly threshold_type: StockAlertType;
  readonly triggered_at: string;
  readonly current_qty: number;
  readonly threshold_qty: number;
  readonly status: StockAlertStatus;
  readonly resolved_at: string | null;
  readonly dismissed_by_enc_id: string | null;
  readonly dismissed_at: string | null;
}

export interface ListAlertsParams {
  /** Server accepts a single value or csv (`active,dismissed`). */
  status?: string;
  yard_enc_id?: string;
  product_enc_id?: string;
  page?: number;
  per_page?: number;
}

interface ListAlertsResponse {
  stock_alerts: StockAlertDto[];
  pagination: Pagination;
}

function alertsQs(params: ListAlertsParams): string {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.yard_enc_id) sp.set('yard_enc_id', params.yard_enc_id);
  if (params.product_enc_id) sp.set('product_enc_id', params.product_enc_id);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

interface UseStockAlertsOpts {
  /** When true (Alerts tab active), poll every 60s. */
  enablePolling?: boolean;
}

// Server emits `{ data: rows, total, page, per_page }` from
// `success(listStockAlertsForActor(...))`. Older docs / test mocks
// shaped this as `{ stock_alerts, pagination: { ... total_pages } }`.
// We normalise to the contract shape so both wire formats are accepted.
function normaliseAlertsList(body: unknown): ListAlertsResponse {
  const raw = (body ?? {}) as {
    stock_alerts?: StockAlertDto[];
    data?: StockAlertDto[];
    pagination?: Partial<Pagination>;
    total?: number;
    page?: number;
    per_page?: number;
    total_pages?: number;
  };
  const stock_alerts = raw.stock_alerts ?? raw.data ?? [];
  const page = raw.pagination?.page ?? raw.page ?? 1;
  const per_page =
    raw.pagination?.per_page ?? raw.per_page ?? stock_alerts.length;
  const total = raw.pagination?.total ?? raw.total ?? stock_alerts.length;
  const total_pages =
    raw.pagination?.total_pages ??
    raw.total_pages ??
    (per_page > 0 ? Math.max(1, Math.ceil(total / per_page)) : 1);
  return { stock_alerts, pagination: { page, per_page, total, total_pages } };
}

export function useWbStockAlerts(
  params: ListAlertsParams = {},
  opts: UseStockAlertsOpts = {},
) {
  const options: UseQueryOptions<ListAlertsResponse> = {
    queryKey: queryKeys.wb.stockAlerts(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<unknown>(
        `${endpoints.wb.stockAlerts}${alertsQs(params)}`,
      );
      return normaliseAlertsList(res.data);
    },
    staleTime: 15_000,
    // Polling: 60s when the caller signals the Alerts tab is active.
    // Setting `false` disables the interval, so React Query stops the
    // background refetch when the user is on Balances / Movements.
    refetchInterval: opts.enablePolling ? 60_000 : false,
    refetchIntervalInBackground: false,
  };
  return useQuery(options);
}

// -- Dismiss mutation -------------------------------------------------------

export function useDismissStockAlert() {
  const qc = useQueryClient();
  return useMutation<{ stock_alert: StockAlertDto }, Error, string>({
    mutationFn: async (encId) => {
      const res = await apiClient.post<{ stock_alert: StockAlertDto }, undefined>(
        endpoints.wb.stockAlertDismiss(encId),
        undefined,
      );
      return res.data;
    },
    onSuccess: () => {
      // Invalidate every alerts list — filters vary; one broad bucket
      // keeps the cache consistent without enumerating each filter combo.
      void qc.invalidateQueries({ queryKey: ['wb', 'stock-alerts'] });
    },
  });
}
